import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';

import { UserService } from './user.service';

/** How often the token's remaining life is checked. */
export const TOKEN_REFRESH_CHECK_MS = 60_000;
/** Renew when this little life is left. */
export const TOKEN_REFRESH_WINDOW_MS = 5 * 60_000;

/**
 * Keeps a live session's API token fresh by renewing it through `/auth/refresh` shortly BEFORE it
 * expires, so the short token lifetime (15 min) never reaches the user and `verify-email` — which
 * needs the client secret — is only the fallback for a token that already lapsed (laptop asleep).
 *
 * A once-a-minute check rather than one timer set per token: it needs no hook into every place a
 * token is stored, and a background tab's throttled timers only delay a renewal, never skip it.
 * Runs outside the Angular zone so a long-lived interval never holds the app unstable; started once
 * from `AppComponent` after first render, so it never runs on the server.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private readonly userService = inject(UserService);
  private readonly zone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);

  private started = false;
  private inFlight = false;

  /** Idempotent. Call once from the browser after first render. */
  init(): void {
    if (!isPlatformBrowser(this.platformId) || this.started) return;
    this.started = true;
    this.zone.runOutsideAngular(() => {
      setInterval(() => this.tick(), TOKEN_REFRESH_CHECK_MS);
    });
  }

  /** One check; exposed for tests. */
  tick(): void {
    if (this.inFlight || !this.userService.isSessionVerified()) return;
    const expiry = this.userService.getTokenExpiryMs();
    if (expiry === null) return;
    const left = expiry - Date.now();
    // Already expired: leave it to ensureValidToken's normal re-mint on the next API call.
    if (left <= 0 || left > TOKEN_REFRESH_WINDOW_MS) return;

    this.inFlight = true;
    this.userService.refreshLiveToken().subscribe({
      next: () => { this.inFlight = false; },
      // Not fatal: the token is still valid for a while and the expired-token path remains.
      error: () => { this.inFlight = false; },
    });
  }
}
